import Select from '@/components/Select'
import TextField from '@/components/TextField'
import { Box, Pagination } from '@mui/material'
import { useEffect } from 'react'
import { PartnerType, useAdmin } from '../hooks/useAdmin'
import { usePartnerDetails } from '../hooks/usePartnerDetails'
import { getPartnerDetails } from '../repository/getPartnerDetails'
import Partner from '../types/Partner'
import PartnersList from './PartnersList'

type PartnerAttributes = {
  name: string
  hostname?: string
  company?: string
  organization_id: number
}

type RawPartner = {
  id: string | number
  type: string
  attributes: PartnerAttributes
}

const styles = {
  container: {
    paddingTop: '20px',
    display: 'grid',
    gridTemplateColumns: '1fr 6fr',
    gap: '20px'
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  pagination: {
    mt: '25px',
    alignSelf: 'center'
  }
}

export const TEXT_ON_CHANGE_DELAY = 600

export const Admin = () => {
  const {
    partners,
    totalPages,
    partnerType,
    pagination,
    filter,
    sort,
    setPage,
    setPartnerType,
    setFilters,
    fetchPartners,
    selectPartner
  } = useAdmin()

  const { setPartnerDetails } = usePartnerDetails()

  const PartnerDetailsReducer = (data: RawPartner[]) => {
    if (data.length <= 1) {
      return []
    }

    const roleMap: Record<string, string> = {
      publishers: 'Publisher',
      affiliates: 'Affiliate',
      vendors: 'Vendor'
    }

    return data.map((partner: RawPartner) => ({
      id: Number(partner.id),
      name: partner.attributes.name,
      hostname: partner.attributes?.hostname,
      company: partner.attributes?.company,
      organization: {
        databaseId: partner.attributes?.organization_id
      },
      type: roleMap[partner.type]
    }))
  }

  const handlePartnerClick = async (partner: Partner) => {
    try {
      selectPartner(partner)
      const response = await getPartnerDetails()
      if ('included' in response) {
        const partnerDetails = PartnerDetailsReducer(response.included)
        setPartnerDetails(partnerDetails)
      }
    } catch (error) {
      console.error('Error fetching partner details:', error)
    }
  }

  useEffect(() => {
    fetchPartners()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sort, pagination, partnerType])

  return (
    <Box sx={styles.container}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Select
          fullWidth
          testId="partner-type-select"
          label="Partner Type"
          value={partnerType}
          options={['affiliates', 'publishers', 'vendors']}
          optionLabels={['Affiliates', 'Publishers', 'Vendors']}
          onChange={(option) => setPartnerType(option as PartnerType)}
        />

        <TextField
          fullWidth
          testId="name-text-field"
          label="Name"
          onChangeDelay={TEXT_ON_CHANGE_DELAY}
          onChange={(value) => setFilters('q', value)}
        />

        <TextField
          fullWidth
          testId="id-text-field"
          label="ID"
          type="number"
          onChangeDelay={TEXT_ON_CHANGE_DELAY}
          onChange={(value) => setFilters('id', value)}
        />
      </Box>
      <Box sx={styles.listContainer}>
        <PartnersList
          partners={partners}
          showCompanyName={partnerType !== 'publishers'}
          onClick={handlePartnerClick}
        />
        <Pagination
          page={pagination.page}
          count={totalPages}
          sx={styles.pagination}
          onChange={(_, page) => setPage(page)}
        />
      </Box>
    </Box>
  )
}
